import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { API_ENDPOINTS } from '../constants/api';
import axiosInstance from '../services/client';
import { useAlert } from './useAlert';

export const useDownloadTaxProfile = () => {
    const { t } = useTranslation();
    const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});
    const alert = useAlert();

    const downloadTaxProfile = useCallback(
        async (documentId: string, fileName: string) => {
            if (loadingStates[documentId]) {
                return;
            }

            try {
                setLoadingStates((prev) => ({
                    ...prev,
                    [documentId]: true
                }));

                const response = await axiosInstance.get(
                    `${API_ENDPOINTS.DOWNLOAD_TAX_PROFILE}?documentId=${documentId}`,
                    {
                        responseType: 'blob',
                        withCredentials: true
                    }
                );

                // Create download link
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();

                link.remove();
                window.URL.revokeObjectURL(url);

                // Clear loading state after successful download
                setLoadingStates((prev) => ({
                    ...prev,
                    [documentId]: false
                }));
                alert.success(t('epic.esport.profile.tax.form.download.success'));
            } catch (error) {
                console.error('Error downloading tax document:', error);

                // Clear loading state on error
                setLoadingStates((prev) => ({
                    ...prev,
                    [documentId]: false
                }));
                alert.error(t('epic.common.network.error'));
            }
        },
        [loadingStates, t]
    );

    const clearLoadingState = useCallback((documentId: string) => {
        setLoadingStates((prev) => ({
            ...prev,
            [documentId]: false
        }));
    }, []);

    return {
        loadingStates,
        downloadTaxProfile,
        clearLoadingState
    };
};
